/**
 * Join + sign-in choice modals.
 *
 * - Any [data-dw-join] element (or the #/join hash) opens the Apply to Join
 *   dialog and renders the HubSpot form inside it. The element's href stays as
 *   the no-JS fallback (georgian.io/community).
 * - Any [data-dw-signin-choice] element (or the #/signin hash) opens the
 *   sign-in choice dialog (Member Sign In vs Request to Join).
 * - Clicking a [data-portal] link inside one of our dialogs closes the dialog
 *   first so Ghost Portal isn't hidden behind it (dialogs live in the top layer).
 *
 * Exposes window.dwJoinModal.open() for other scripts (signin-monitor.js).
 */
(function () {
  var HS_SCRIPT = "https://js.hsforms.net/forms/embed/v2.js";

  var joinDialog = document.getElementById("dw-join-modal");
  var choiceDialog = document.getElementById("dw-signin-choice-modal");
  if (!joinDialog && !choiceDialog) return;

  var formCreated = false;
  var scriptPromise = null;

  function config() {
    return window.__dwHubspot || {};
  }

  function setStatus(text) {
    var el = joinDialog && joinDialog.querySelector("[data-dw-join-status]");
    if (el) el.textContent = text;
  }

  function loadHubspot() {
    if (window.hbspt && window.hbspt.forms) return Promise.resolve();
    if (scriptPromise) return scriptPromise;
    scriptPromise = new Promise(function (resolve, reject) {
      var s = document.createElement("script");
      s.src = HS_SCRIPT;
      s.async = true;
      s.onload = function () {
        // hbspt can appear a tick after onload on mobile Safari
        var tries = 0;
        (function check() {
          if (window.hbspt && window.hbspt.forms) return resolve();
          if (++tries > 50) return reject(new Error("hbspt unavailable"));
          setTimeout(check, 100);
        })();
      };
      s.onerror = reject;
      document.head.appendChild(s);
    });
    scriptPromise.catch(function () {
      scriptPromise = null;
    });
    return scriptPromise;
  }

  function createForm() {
    var cfg = config();
    if (formCreated) return;
    if (!cfg.portalId || !cfg.joinFormId) {
      setStatus("The application form is unavailable right now.");
      return;
    }
    formCreated = true;
    loadHubspot()
      .then(function () {
        var container = document.getElementById("dw-join-form");
        var status = container.querySelector("[data-dw-join-status]");
        // HubSpot's iframe-based forms don't reliably fire onFormReady, so
        // drop the loading text as soon as the form's markup lands.
        var observer = new MutationObserver(function () {
          if (container.querySelector("iframe, form")) {
            if (status) status.remove();
            observer.disconnect();
          }
        });
        observer.observe(container, { childList: true, subtree: true });
        window.hbspt.forms.create({
          portalId: cfg.portalId,
          formId: cfg.joinFormId,
          region: cfg.region || "na1",
          target: "#dw-join-form",
        });
      })
      .catch(function () {
        formCreated = false;
        setStatus(
          "We couldn't load the application form. Please check your connection and try again.",
        );
      });
  }

  function openDialog(dialog) {
    if (!dialog || dialog.open) return;
    [joinDialog, choiceDialog].forEach(function (d) {
      if (d && d !== dialog && d.open) d.close();
    });
    if (typeof dialog.showModal === "function") {
      dialog.showModal();
    } else {
      dialog.setAttribute("open", "");
    }
    document.documentElement.classList.add("dw-modal-open");
  }

  function closeDialog(dialog) {
    if (!dialog || !dialog.open) return;
    if (typeof dialog.close === "function") dialog.close();
    else dialog.removeAttribute("open");
  }

  function openJoin() {
    if (!joinDialog) return false;
    openDialog(joinDialog);
    createForm();
    return true;
  }

  function openChoice() {
    if (!choiceDialog) return false;
    openDialog(choiceDialog);
    return true;
  }

  function clearHash(hash) {
    if (window.location.hash === hash && window.history.replaceState) {
      window.history.replaceState(
        null,
        "",
        window.location.pathname + window.location.search,
      );
    }
  }

  [joinDialog, choiceDialog].forEach(function (dialog) {
    if (!dialog) return;
    dialog.addEventListener("close", function () {
      if (
        !(joinDialog && joinDialog.open) &&
        !(choiceDialog && choiceDialog.open)
      ) {
        document.documentElement.classList.remove("dw-modal-open");
      }
      clearHash(dialog === joinDialog ? "#/join" : "#/signin");
    });
    // Backdrop click: the click target is the <dialog> itself, not the panel.
    dialog.addEventListener("click", function (e) {
      if (e.target === dialog) closeDialog(dialog);
    });
  });

  document.addEventListener("click", function (e) {
    var target = e.target.closest
      ? e.target.closest(
          "[data-dw-join], [data-dw-signin-choice], [data-dw-modal-close], .dw-modal [data-portal]",
        )
      : null;
    if (!target) return;

    if (target.hasAttribute("data-dw-modal-close")) {
      closeDialog(target.closest("dialog"));
      return;
    }
    if (target.hasAttribute("data-portal")) {
      // Let Portal handle the click; just get our dialog out of its way.
      closeDialog(target.closest("dialog"));
      return;
    }
    if (target.hasAttribute("data-dw-join")) {
      if (openJoin()) e.preventDefault();
      return;
    }
    if (target.hasAttribute("data-dw-signin-choice")) {
      if (openChoice()) e.preventDefault();
    }
  });

  function handleHash() {
    if (window.location.hash === "#/join") openJoin();
    else if (window.location.hash === "#/signin") openChoice();
  }
  window.addEventListener("hashchange", handleHash);
  handleHash();

  /* Prefetch the HubSpot embed script so the form renders quickly on open:
     on idle after page load, and immediately on hover/focus/touch of a join
     or sign-in trigger. Skipped for signed-in members and Save-Data users
     (the intent listeners still apply to them). */
  function prefetch() {
    if (!joinDialog || !config().joinFormId) return;
    loadHubspot().catch(function () {});
  }

  ["pointerover", "focusin", "touchstart"].forEach(function (type) {
    document.addEventListener(
      type,
      function (e) {
        if (
          e.target.closest &&
          e.target.closest("[data-dw-join], [data-dw-signin-choice]")
        ) {
          prefetch();
        }
      },
      { passive: true },
    );
  });

  // window.__dwMember is set after this bundle runs, so check it on load.
  function idlePrefetch() {
    var saveData = navigator.connection && navigator.connection.saveData;
    if (window.__dwMember || saveData) return;
    if (window.requestIdleCallback) {
      window.requestIdleCallback(prefetch, { timeout: 4000 });
    } else {
      setTimeout(prefetch, 2000);
    }
  }
  if (document.readyState === "complete") idlePrefetch();
  else window.addEventListener("load", idlePrefetch);

  window.dwJoinModal = { open: openJoin, openChoice: openChoice };
})();
